import { getApiGatewayUrl } from './apiGatewayUrl';
import type { TokenMetadata } from '../types/models/User';

/**
 * Single-flight session refresh.
 *
 * The access token lives in a short-lived HttpOnly cookie (lifetime owned by the
 * issuer, HeliosAccessTokenOptions.Lifetime) and is renewed by calling
 * POST /api/auth/refresh. Nothing here assumes a particular lifetime — the expiry
 * is read off the response. Two mechanisms need to trigger a refresh:
 *
 *  1. `useTokenRefresh` — proactively, ~5 min before expiry.
 *  2. The axios / orval 401 handlers — reactively, the instant a request finds
 *     the cookie already expired (the gap the proactive timer can miss, e.g. a
 *     throttled background tab or a burst of concurrent requests).
 *
 * These can fire at the same moment (classic case: the user re-focuses a tab
 * whose proactive timer was throttled AND immediately clicks something). The
 * backend ROTATES the refresh token on every successful refresh, marking the
 * presented one used and — on detecting a replay — revoking the WHOLE TOKEN
 * FAMILY (see HeliosTokenLifecycleService.RefreshAsync, ADR-028). So two
 * concurrent refreshes both present the same token; the first rotates it, and
 * the second is read as reuse and kills the session the first just renewed.
 *
 * Coordination therefore has to work at two levels:
 *
 *  - WITHIN a tab: `inFlight` funnels all callers onto one shared promise, so a
 *    proactive refresh and a burst of 401s make exactly one request.
 *  - ACROSS tabs: `inFlight` is module state, so it is per JS context and does
 *    nothing for a second tab holding the same cookie. Two tabs run independent
 *    `useTokenRefresh` timers, so without cross-tab coordination the slower one
 *    replays a token the faster one rotated seconds earlier and BOTH tabs get
 *    logged out. `withCrossTabLock` closes that (ADR-028 finding F9).
 *
 * The backend also carries a short reuse grace window, so neither layer alone is
 * load-bearing — but the client should not be generating races for the server to
 * absorb, and the grace window cannot help a tab that has already been logged out.
 *
 * Uses native `fetch` (NOT axiosInstance) on purpose: a refresh that itself
 * receives a 401 must not re-enter the axios response interceptor, or it would
 * recurse / double-dispatch the logout event.
 */

const CSRF_COOKIE_NAME = 'xsrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const REFRESH_ENDPOINT = '/api/auth/refresh';
const TOKEN_METADATA_KEY = 'token_metadata';
/** Web Locks name shared by every Helios tab in this origin. */
const REFRESH_LOCK_NAME = 'helios-session-refresh';

export interface SessionRefreshResult {
  /** True when the server issued a fresh access cookie. */
  ok: boolean;
  /**
   * HTTP status of the refresh call, when a response was received. Absent on a
   * network error. Callers use `status === 401` to tell a dead session
   * (log out) apart from a transient failure (keep session, maybe retry).
   */
  status?: number;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined' || !document.cookie) {
    return null;
  }
  const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!cookie) {
    return null;
  }
  const [, value] = cookie.split('=');
  return decodeURIComponent(value);
}

/**
 * Persist the new expiry so the proactive timer schedules the next refresh
 * correctly. Best-effort — a missing / non-JSON body must not fail a refresh
 * the server already honoured.
 */
function syncTokenMetadata(expiresAt: string): void {
  try {
    const raw = localStorage.getItem(TOKEN_METADATA_KEY);
    const meta = (raw ? JSON.parse(raw) : {}) as Partial<TokenMetadata>;
    localStorage.setItem(TOKEN_METADATA_KEY, JSON.stringify({ ...meta, expiresAt }));
  } catch {
    // Ignore parse / storage errors — the cookie is what actually authenticates.
  }
}

async function performRefresh(): Promise<SessionRefreshResult> {
  const baseUrl = getApiGatewayUrl();
  const csrfToken = getCookie(CSRF_COOKIE_NAME);

  let response: Response;
  try {
    response = await fetch(`${baseUrl}${REFRESH_ENDPOINT}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Correlation-Id': crypto.randomUUID(),
        ...(csrfToken ? { [CSRF_HEADER_NAME]: csrfToken } : {}),
      },
    });
  } catch {
    // Network error — transient, no HTTP status to report.
    return { ok: false };
  }

  if (!response.ok) {
    return { ok: false, status: response.status };
  }

  try {
    const data = (await response.json()) as { expiresAt?: string };
    if (data?.expiresAt) {
      syncTokenMetadata(data.expiresAt);
    }
  } catch {
    // Body parsing is best-effort; the refresh still succeeded.
  }

  return { ok: true, status: response.status };
}

/**
 * Read the currently-known access-token expiry. Used as the cross-tab signal for
 * "did somebody else already refresh?" — `syncTokenMetadata` writes it on every
 * successful refresh, so a change is proof another tab succeeded, and it needs no
 * new storage key or message protocol of its own.
 */
function readExpiresAt(): string | null {
  try {
    const raw = localStorage.getItem(TOKEN_METADATA_KEY);
    if (!raw) {
      return null;
    }
    const meta = JSON.parse(raw) as Partial<TokenMetadata>;
    return meta.expiresAt ?? null;
  } catch {
    return null;
  }
}

/**
 * Serialise refreshes across tabs, and skip redundant ones.
 *
 * Uses the Web Locks API — the browser primitive for exactly this, and the only
 * option that is correct rather than merely usually-right: a localStorage mutex
 * cannot release reliably when a tab is killed mid-refresh, and a BroadcastChannel
 * alone has no way to elect a single winner.
 *
 * Holding the lock is NOT sufficient on its own. A tab that waits its turn would
 * otherwise proceed to refresh with the cookie value the winner has already
 * rotated away — the exact replay this is meant to prevent, merely serialised. So
 * after acquiring the lock it re-reads the expiry: if it moved, another tab
 * refreshed while we waited and its result is already in the shared cookie, so
 * there is nothing to do but adopt it.
 *
 * Degrades to an uncoordinated refresh where `navigator.locks` is unavailable
 * (older Safari, some embedded webviews) — no worse than before, and the server's
 * grace window still covers the race.
 */
async function withCrossTabLock(): Promise<SessionRefreshResult> {
  if (typeof navigator === 'undefined' || !navigator.locks?.request) {
    return performRefresh();
  }

  const expiryBeforeLock = readExpiresAt();

  try {
    return await navigator.locks.request(REFRESH_LOCK_NAME, async () => {
      const expiryAfterLock = readExpiresAt();
      if (expiryAfterLock !== null && expiryAfterLock !== expiryBeforeLock) {
        // Another tab refreshed while we queued. The cookie it set is shared, so
        // this tab is already authenticated — issuing our own request here would
        // replay a rotated token and risk taking both sessions down.
        return { ok: true, status: 200 };
      }
      return performRefresh();
    });
  } catch {
    // A lock can be rejected if the page is being torn down. Falling back keeps a
    // refresh from being silently dropped at the moment it is most needed.
    return performRefresh();
  }
}

let inFlight: Promise<SessionRefreshResult> | null = null;

/**
 * Refresh the session, coalescing concurrent callers within this tab onto a single
 * request and serialising against other tabs. Never rejects — always resolves to a
 * {@link SessionRefreshResult}.
 */
export function refreshSession(): Promise<SessionRefreshResult> {
  if (inFlight) {
    return inFlight;
  }
  inFlight = withCrossTabLock().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
